import { getLastRequestId, getLastTraceparent } from './traceContext';

export type CaptureEvent =
  // ── Capture funnel ──
  | 'capture_start'
  | 'capture_stop'
  | 'capture_error'
  | 'review_play_attempt'
  | 'review_play_blocked'
  | 'audio_track_lost'
  | 'audio_track_degraded'
  | 'cover_auto_selected'
  | 'cover_user_selected'
  | 'upload_started'
  | 'upload_completed'
  | 'upload_failed'
  | 'prompt_response_submitted'
  | 'prompt_auto_advance_cancelled'
  | 'prompt_flow_exited'
  | 'prompt_set_covered'
  | 'prompt_sync_confirmed'
  | 'prompt_sync_failed'
  | 'arc_selection_recovered'
  // ── Runtime errors (C3) ──
  // A JS exception used to white-screen the guest acquisition funnel with no
  // signal anywhere, and no funnel-silence alert to catch the absence either.
  | 'js_error'
  | 'unhandled_rejection'
  | 'render_error'
  | 'api_error'
  | 'session_lost'
  // ── Join funnel (C4) ──
  | 'page_view'
  | 'join_link_opened'
  | 'code_submitted'
  | 'code_accepted'
  | 'code_rejected'
  | 'session_created'
  | 'thread_viewed'
  | 'composer_opened'
  // ── Paywall (C4) — the denominator for the documented >2% conversion KPI ──
  | 'paywall_impression'
  | 'checkout_started'
  // ── Push (C4) ──
  | 'push_permission_granted'
  | 'push_permission_denied'
  | 'push_subscribe_failed'
  // ── Offline queue (C4) ──
  | 'queue_sync_failed'
  | 'queue_item_abandoned'
  | 'queue_sync_skipped_no_session'
  // ── Service worker (C4) ──
  | 'sw_register_failed'
  | 'sw_update_check_failed'
  | 'sw_activation_timeout'
  // ── Web vitals (C4) ──
  | 'web_vital';

const sessionId = crypto.randomUUID();
const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 1_000;
/**
 * Ceiling on the retry queue. A backend outage must not grow this without
 * bound and push the tab into memory pressure — telemetry is never worth
 * degrading the app it observes.
 */
const MAX_PENDING = 200;
/** Backoff after a failed flush, so a 500ing backend isn't hammered in a tight loop. */
const RETRY_DELAY_MS = 15_000;
const STORAGE_KEY = 'ourtake_pending_telemetry';

type Severity = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

type ClientEvent = {
  eventId: string;
  sessionId: string;
  traceparent?: string;
  timestamp: string;
  platform: 'pwa';
  appVersion: string | undefined;
  surface: string;
  action: CaptureEvent;
  result: string;
  severity: Severity;
  entityIds: Record<string, string>;
  attributes: Record<string, unknown>;
};

let pendingEvents: ClientEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

// Events that report a *successful* recovery from a state that should not have
// occurred. The substring heuristics below would score these info/success and
// bury them in the dashboard, which defeats the point — they exist to surface a
// backend regression the client papered over. Classify them explicitly.
const RECOVERY_EVENTS = new Set<CaptureEvent>(['arc_selection_recovered']);

/**
 * Events whose severity the substring heuristics get wrong.
 * `js_error`/`unhandled_rejection`/`render_error` contain "error" and so would
 * already score `error`, but they are listed for explicitness; the rest are
 * cases where the name carries no signal at all.
 */
const EXPLICIT_SEVERITY: Partial<Record<CaptureEvent, Severity>> = {
  js_error: 'error',
  unhandled_rejection: 'error',
  render_error: 'fatal', // took a whole subtree down
  session_lost: 'warning',
  push_permission_denied: 'info', // a user choice, not a fault
  queue_item_abandoned: 'error', // a contribution the user made is gone
  queue_sync_skipped_no_session: 'warning',
  sw_activation_timeout: 'warning',
  web_vital: 'debug',
  page_view: 'debug',
};

const EXPLICIT_RESULT: Partial<Record<CaptureEvent, string>> = {
  push_permission_denied: 'declined',
  code_rejected: 'failure',
  queue_sync_skipped_no_session: 'degraded',
  session_lost: 'degraded',
};

function eventSeverity(event: CaptureEvent): Severity {
  const explicit = EXPLICIT_SEVERITY[event];
  if (explicit) return explicit;
  if (RECOVERY_EVENTS.has(event)) return 'warning';
  if (event.includes('failed') || event.includes('error') || event.includes('lost')) return 'error';
  if (event.includes('blocked') || event.includes('degraded') || event.includes('denied')) return 'warning';
  return 'info';
}

function eventResult(event: CaptureEvent): string {
  const explicit = EXPLICIT_RESULT[event];
  if (explicit) return explicit;
  if (RECOVERY_EVENTS.has(event)) return 'degraded';
  if (event.includes('failed') || event.includes('error') || event.includes('lost')) return 'failure';
  if (event.includes('blocked') || event.includes('degraded')) return 'degraded';
  return 'success';
}

function extractEntityIds(metadata: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of ['spaceId', 'threadId', 'userId', 'contributionId', 'requestId']) {
    const value = metadata[key];
    if (typeof value === 'string' && value.length > 0) result[key] = value;
  }
  return result;
}

/**
 * Persist the queue so a tab close (or a crash) doesn't lose the very events
 * that explain it — an unhandled error is usually followed immediately by the
 * user leaving.
 */
function persistPending(): void {
  try {
    if (pendingEvents.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingEvents.slice(0, MAX_PENDING)));
  } catch {
    // Storage full / disabled (private mode). Telemetry must degrade silently.
  }
}

function restorePending(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as ClientEvent[];
    if (Array.isArray(parsed) && parsed.length > 0) {
      pendingEvents = parsed.slice(0, MAX_PENDING).concat(pendingEvents);
    }
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Corrupt payload — drop it rather than blocking startup.
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}

/** Mirrors api/client's cookie read; duplicated to avoid an import cycle. */
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Send the batch in a way that survives the page going away.
 *
 * `fetch(..., { keepalive: true })` — NOT `navigator.sendBeacon`. Beacon looks
 * like the obvious choice, and the plan named it first, but it cannot set
 * headers: it would post `text/plain` with no `X-CSRF-Token`, and the v2 router
 * applies `csrfProtection` to `/telemetry` (app.ts). Every beaconed batch would
 * be rejected — while `sendBeacon` returning `true` (queued, not delivered)
 * made the client discard the events as sent. That is strictly worse than the
 * bug it was meant to fix.
 *
 * `keepalive` gives us the same outlive-the-document guarantee WITH headers.
 * Its 64KB body cap is far above our ≤20-event batch.
 *
 * Returns false when the request could not even be STARTED, so the caller keeps
 * the events rather than assuming delivery. Starting is not delivering, though:
 * a keepalive fetch still rejects asynchronously (offline, refused, over the
 * 64KB cap). Swallowing that rejection would repeat, in a new shape, the very
 * bug that disqualified `sendBeacon` — treating "queued" as "sent" and dropping
 * the batch. So the rejection re-queues and re-persists. On a real unload the
 * handler may never get to run, but `pagehide` also fires for bfcache and tab
 * switches, where it runs fine and the events survive to the next flush.
 */
function sendKeepalive(events: ClientEvent[]): boolean {
  try {
    if (typeof fetch !== 'function') return false;
    const base = import.meta.env.VITE_API_URL || '/api/v2';
    const csrf = readCsrfToken();
    void fetch(`${base}/telemetry/client-events`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
      body: JSON.stringify({ events }),
    })
      .then((response) => {
        // A 4xx/5xx is a non-delivery too — notably the 403 a missing CSRF
        // token produces, which is the failure this whole path was rewritten
        // to avoid and must therefore never look like success.
        if (!response || response.ok) return;
        requeue(events);
      })
      .catch(() => requeue(events));
    return true;
  } catch {
    return false;
  }
}

/** Put a failed batch back at the FRONT and re-persist. Never throws. */
function requeue(events: ClientEvent[]): void {
  try {
    pendingEvents = events.concat(pendingEvents).slice(0, MAX_PENDING);
    persistPending();
  } catch {
    // localStorage full or unavailable — nothing further to try.
  }
}

function scheduleRetry(): void {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    flushEvents();
  }, RETRY_DELAY_MS);
}

export function flushEvents(options: { useBeacon?: boolean } = {}): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pendingEvents.length === 0) return;

  const events = pendingEvents.splice(0, MAX_BATCH_SIZE);

  if (options.useBeacon) {
    if (!sendKeepalive(events)) {
      // Put them back and persist — the tab is going away, so localStorage is
      // the only remaining chance to deliver them on the next load.
      pendingEvents = events.concat(pendingEvents).slice(0, MAX_PENDING);
    }
    persistPending();
    return;
  }

  // Imported lazily rather than at module scope. api/client imports THIS module
  // (for api_error / session_lost), so a static import would close a cycle —
  // and it would drag the whole API client into the module graph of every
  // component that merely wants to emit an event, which broke page tests that
  // mock api/client without re-exporting apiFetch.
  void import('../api/client')
    .then(({ apiFetch }) => apiFetch('/telemetry/client-events', {
      method: 'POST',
      suppressAuthRedirect: true,
      skipTraceCapture: true,
      body: JSON.stringify({ events }),
    }))
    .then(() => {
      if (pendingEvents.length > 0) flushEvents();
    })
    .catch(() => {
      // Previously the batch was spliced out and then DESTROYED — the events
      // most worth having (the ones sent while the backend was unhealthy) were
      // exactly the ones dropped. The console fallback didn't help either:
      // esbuild's `pure` list strips console.info from production bundles, so
      // it was a no-op where it mattered.
      pendingEvents = events.concat(pendingEvents).slice(0, MAX_PENDING);
      persistPending();
      console.warn('[capture-analytics] flush failed; will retry', { queued: pendingEvents.length });
      scheduleRetry();
    });
}

function scheduleFlush(): void {
  if (pendingEvents.length >= MAX_BATCH_SIZE) {
    flushEvents();
  } else if (!flushTimer) {
    flushTimer = setTimeout(() => flushEvents(), FLUSH_INTERVAL_MS);
  }
}

if (typeof window !== 'undefined') {
  restorePending();
  // Beacon on the way out; fetch would be aborted by the navigation.
  window.addEventListener('pagehide', () => flushEvents({ useBeacon: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents({ useBeacon: true });
  });
}

export interface TrackOptions {
  /** Override the name-derived severity (e.g. an api_error that is really fatal). */
  severity?: Severity;
  /** Override the name-derived result. */
  result?: string;
}

export function trackCaptureEvent(
  event: CaptureEvent,
  metadata: Record<string, unknown> = {},
  options: TrackOptions = {},
): void {
  const traceparent = getLastTraceparent() ?? undefined;
  const requestId = getLastRequestId();
  const attributes = {
    ...metadata,
    ...(requestId ? { requestId } : {}),
  };

  pendingEvents.push({
    eventId: crypto.randomUUID(),
    sessionId,
    traceparent,
    timestamp: new Date().toISOString(),
    platform: 'pwa',
    appVersion: import.meta.env.VITE_APP_VERSION,
    surface: typeof metadata.surface === 'string' ? metadata.surface : 'capture',
    action: event,
    result: options.result ?? eventResult(event),
    severity: options.severity ?? eventSeverity(event),
    entityIds: extractEntityIds(metadata),
    attributes,
  });
  // Bound the queue at the ingest point too, so a runaway emitter (an error
  // loop firing js_error every frame) cannot grow it without limit.
  if (pendingEvents.length > MAX_PENDING) {
    pendingEvents = pendingEvents.slice(-MAX_PENDING);
  }
  scheduleFlush();
}

/** Test seam: drop all queued events and timers. */
export function __resetCaptureAnalyticsForTest(): void {
  pendingEvents = [];
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/** Test seam: current queue depth. */
export function __pendingCountForTest(): number {
  return pendingEvents.length;
}
